import React from "react";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { Filter } from "lucide-react";
import "./pih-style-table.css";

export type SortOrder = "ascend" | "descend" | null;

export interface PihColumnTitleOptions {
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  sortField?: string;
  sortOrder?: SortOrder;
  showFilterIcon?: boolean;
  filterActive?: boolean;
  onFilterIconClick?: (event: React.MouseEvent) => void;
  glossaryKey?: string;
  glossaryAffordance?: React.ReactNode;
  hideFilterInput?: boolean;
  onColumnSort?: (dataIndex: string) => void;
}

export function renderPihColumnTitle(title: string, dataIndex: string, options: PihColumnTitleOptions) {
  const {
    columnFilters,
    onColumnFilterChange,
    sortField,
    sortOrder,
    showFilterIcon,
    filterActive,
    onFilterIconClick,
    glossaryAffordance,
    hideFilterInput,
    onColumnSort,
  } = options;

  return (
    <div className="column-header-with-filter">
      <div
        className={`column-header-text-wrapper${onColumnSort ? " column-header-text-wrapper--sortable" : ""}`}
        onClick={
          onColumnSort
            ? (event) => {
                event.stopPropagation();
                onColumnSort(dataIndex);
              }
            : undefined
        }
        onKeyDown={
          onColumnSort
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onColumnSort(dataIndex);
                }
              }
            : undefined
        }
        role={onColumnSort ? "button" : undefined}
        tabIndex={onColumnSort ? 0 : undefined}
      >
        <span className="column-header-title-with-info">
          {title}
          {glossaryAffordance ?? null}
        </span>
        <span className="custom-sort-icon">
          {sortField === dataIndex && sortOrder === "ascend" && <CaretUpOutlined />}
          {sortField === dataIndex && sortOrder === "descend" && <CaretDownOutlined />}
          {(!sortField || sortField !== dataIndex || !sortOrder) && (
            <span className="sort-icon-placeholder">
              <CaretUpOutlined />
            </span>
          )}
        </span>
        {showFilterIcon && (
          <button
            type="button"
            className={`heat-profile-column-filter-icon-wrap${filterActive ? " has-active" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              onFilterIconClick?.(event);
            }}
            aria-label={`Filter ${title}`}
          >
            <Filter size={14} strokeWidth={2} className="heat-profile-column-filter-lucide" />
          </button>
        )}
      </div>
      {!hideFilterInput && (
        <div className="header-filter-input">
          <Input
            placeholder="Search"
            size="small"
            value={columnFilters[dataIndex] ?? ""}
            onChange={(event) => onColumnFilterChange(dataIndex, event.target.value)}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

interface PihTableShellProps {
  title: string;
  description: string;
  itemCount: number;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export function PihTableShell({ title, description, itemCount, actions, children }: PihTableShellProps) {
  return (
    <div className="coil-pih-table-card">
      <div className="heat-profile-list-header">
        <div className="heat-profile-header-left">
          <div className="heat-profile-header-content">
            <div className="heat-profile-title-row">
              <h3 className="heat-profile-list-title">{title}</h3>
              <span className="heat-profile-summary">
                {itemCount} item{itemCount === 1 ? "" : "s"}
              </span>
            </div>
            <p className="heat-profile-list-description">{description}</p>
          </div>
        </div>
        {actions ? <div className="heat-profile-header-actions">{actions}</div> : null}
      </div>
      <div className="heat-profile-table-wrap">{children}</div>
    </div>
  );
}
